// Tile project math + quantified accessory rules.
// Tile size always in inches (North American standard). Area dimensions follow
// the active unit system. Grout formula calibrated to published coverage data:
// 12×12 tile, 3/16" joint → 0.50 lbs/sqft → 25 lb bag ≈ 50 sqft.
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface TileArea {
  name: string
  length: number
  width: number
}

// 'dimensions' = user enters one or more L × W rooms.
// 'area' = user already knows the total floor area and types it directly.
export type TileInputMode = 'dimensions' | 'area'

export interface TileInputs {
  unit: UnitSystem
  inputMode: TileInputMode
  areas: TileArea[]
  totalArea: number     // sq ft (imperial) or m² (metric) — used in 'area' mode
  tileWidthIn: number   // tile width, always inches
  tileLengthIn: number  // tile height/length, always inches
  jointWidthIn: number  // grout joint width, always inches
  wastePct: number
  needBackerBoard: boolean
}

export interface TileResult {
  totalArea: number     // sq ft, before waste
  areaWithWaste: number // sq ft, after waste
  tiles: number
  thinsetBags: number   // 50 lb bags
  groutLbs: number      // pounds needed
  groutBags: number     // 25 lb bags
  backerBoardSheets: number
  spacerBags: number    // bags of 100 cross spacers
  lines: AccessoryLine[]
}

export const DEFAULT_TILE: TileInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  // Name starts empty so the "Select a room" placeholder is the dropdown's
  // default. Dimensions are neutral 10×10 ft until the user picks a room.
  areas: [{ name: '', length: 10, width: 10 }],
  totalArea: 100,
  tileWidthIn: 12,
  tileLengthIn: 12,
  jointWidthIn: 0.1875, // 3/16" standard
  wastePct: 10,
  needBackerBoard: false,
}

// 50 lb bag coverage varies by tile size and trowel notch
function thinsetSqFtPerBag(maxSizeIn: number): number {
  if (maxSizeIn > 15) return 30  // large-format needs 3/8" notch
  if (maxSizeIn > 8)  return 40  // 1/4" square notch standard
  return 50                       // small tile, fine notch
}

const GROUT_FACTOR = 16      // empirical constant calibrated to product coverage charts
const GROUT_BAG_LBS = 25
const BACKER_SQFT = 15       // 3 × 5 ft sheet
const SPACER_PER_BAG = 100

export function computeTile(i: TileInputs, locale: Locale = 'en'): TileResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let totalArea = 0
  if (i.inputMode === 'area') {
    // Direct area entry — m² → sq ft if metric.
    totalArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
  } else {
    for (const a of i.areas ?? []) {
      totalArea += rectArea(toFeet(a.length, i.unit), toFeet(a.width, i.unit))
    }
  }
  const areaWithWaste = applyWaste(totalArea, i.wastePct)

  const tW = Math.max(1, i.tileWidthIn)
  const tH = Math.max(1, i.tileLengthIn)
  const jW = Math.max(0.0625, i.jointWidthIn)

  const tileAreaSqFt = (tW / 12) * (tH / 12)
  const tiles = totalArea > 0 ? ceilTo(areaWithWaste / tileAreaSqFt, 1) : 0

  const sqftPerBag = thinsetSqFtPerBag(Math.max(tW, tH))
  const thinsetBags = totalArea > 0 ? Math.max(1, ceilTo(areaWithWaste / sqftPerBag, 1)) : 0

  // Grout: lbs/sqft = GROUT_FACTOR × jw × (tw + th) / (tw × th)
  // Calibrated: 12×12, 3/16" → 0.5 lbs/sqft; 4×4, 1/8" → 1.0 lbs/sqft
  const groutLbsPerSqFt = GROUT_FACTOR * jW * (tW + tH) / (tW * tH)
  const groutLbs = totalArea > 0 ? round(areaWithWaste * groutLbsPerSqFt, 1) : 0
  const groutBags = totalArea > 0 ? Math.max(1, ceilTo(groutLbs / GROUT_BAG_LBS, 1)) : 0

  const backerBoardSheets = i.needBackerBoard && totalArea > 0
    ? ceilTo(areaWithWaste / BACKER_SQFT, 1)
    : 0

  const spacerBags = totalArea > 0 ? Math.max(1, ceilTo(tiles / SPACER_PER_BAG, 1)) : 0

  const lines: AccessoryLine[] = []

  lines.push({
    item: 'tile thinset mortar', label: tr('Thinset mortar', 'Mortier-colle'), kind: 'consumable',
    qty: thinsetBags, unit: 'bag',
    note: tr(`50 lb bags · ~${sqftPerBag} sq ft per bag · 1/4″ square-notch trowel`, `Sacs de 50 lb · ~${sqftPerBag} pi² par sac · truelle crantée carrée de 1/4 po`),
  })

  lines.push({
    item: jW >= 0.125 ? 'sanded tile grout' : 'unsanded tile grout',
    label: jW >= 0.125 ? tr('Sanded grout', 'Coulis sablé') : tr('Unsanded grout', 'Coulis non sablé'),
    kind: 'consumable',
    qty: groutBags, unit: 'bag',
    note: tr(`≈ ${round(groutLbs, 0)} lbs needed · 25 lb bags`, `≈ ${round(groutLbs, 0)} lb requises · sacs de 25 lb`),
  })

  lines.push({
    item: 'tile spacers cross type', label: tr('Tile spacers', 'Croisillons'), kind: 'consumable',
    qty: spacerBags, unit: 'bag',
    note: tr(`Bags of ${SPACER_PER_BAG} · X-type for consistent ${jW * 16}/16 in joints`, `Sacs de ${SPACER_PER_BAG} · en croix pour des joints réguliers de ${jW * 16}/16 po`),
  })

  if (i.needBackerBoard) {
    lines.push({
      item: 'cement board backer tile', label: tr('Cement backer board', 'Panneau d’appui en ciment'), kind: 'consumable',
      qty: backerBoardSheets, unit: 'sheet',
      note: tr(`3 × 5 ft sheets (${BACKER_SQFT} sq ft each)`, `Feuilles de 3 × 5 pi (${BACKER_SQFT} pi² chacune)`),
    })
  }

  lines.push({
    item: 'tile grout sealer spray', label: tr('Grout sealer', 'Scellant à coulis'), kind: 'consumable',
    qty: 1, unit: 'bottle', note: tr('Apply after grout cures 72 h to protect from stains.', 'Appliquer après 72 h de séchage du coulis pour le protéger des taches.'), optional: true,
  })

  lines.push({ item: 'tile notch trowel 1/4 inch', label: tr('1/4″ notch trowel', 'Truelle crantée 1/4 po'), kind: 'tool', note: tr('Use 3/8″ for tiles larger than 15 in.', 'Utilisez 3/8 po pour les carreaux de plus de 15 po.') })
  lines.push({ item: 'rubber grout float', label: tr('Rubber grout float', 'Taloche à coulis en caoutchouc'), kind: 'tool' })
  lines.push({ item: 'tile wet saw rental', label: tr('Tile wet saw', 'Scie à eau pour carrelage'), kind: 'tool', note: tr('Rentable from big-box stores. Manual cutter for simple straight cuts.', 'Disponible en location dans les grandes surfaces. Un coupe-carreaux manuel suffit pour les coupes droites simples.'), optional: true })
  lines.push({
    item: 'grout sponge large tile', label: tr('Grout sponges', 'Éponges à coulis'), kind: 'consumable',
    qty: Math.max(1, ceilTo(totalArea / 200, 1)), unit: 'sponge',
    note: tr('Large coarse sponge for wiping haze — replace each job.', 'Grande éponge grossière pour essuyer le voile — à remplacer à chaque chantier.'),
    optional: true,
  })

  return {
    totalArea: round(totalArea, 0),
    areaWithWaste: round(areaWithWaste, 0),
    tiles,
    thinsetBags,
    groutLbs,
    groutBags,
    backerBoardSheets,
    spacerBags,
    lines,
  }
}
